import React, { useState, useEffect } from 'react';
import { PersonData, LabelFormat, LabelFormatConfig, ColumnMappingSettings, ColumnMapping } from '../types';
import './LabelPreview.css';

interface LabelPreviewProps {
  data: PersonData[];
  format: LabelFormat;
}

const LabelPreview: React.FC<LabelPreviewProps> = ({ data, format }) => {
  const [spacingAdjustments, setSpacingAdjustments] = useState({
    // 65-1 format spacing
    '65-1': {
      rowSpacing: Array.from({ length: 13 }, (_, i) => {
        if (i >= 9) return 20; // After row 10+ (index 9+)
        if (i >= 5) return 10; // After row 6+ (index 5+)
        return 1; // Default spacing
      }) as number[],
      columnSpacing: [2.5, 2.5, 2.5, 4, 4] as number[]
    },
    // 21-3 format spacing (3 columns, 7 rows)
    '21-3': {
      rowSpacing: Array.from({ length: 7 }, () => 2) as number[], // Default 2mm spacing
      columnSpacing: [2.5, 2.5, 2.5] as number[] // Default spacing for 3 columns
    },
    // 40-2 format spacing (4 columns, 10 rows)
    '40-2': {
      rowSpacing: Array.from({ length: 10 }, () => 2) as number[], // Default 2mm spacing
      columnSpacing: [2.5, 2.5, 2.5, 2.5] as number[] // Default spacing for 4 columns
    },
    showControls: false,
    activeTab: 'columns' // Start with columns tab to help users set up mappings
  });

  const [fontSettings, setFontSettings] = useState({
    diplomaFontSize: {
      '65-1': 9, // pt
      '21-3': 11, // pt
      '40-2': 10 // pt
    },
    nameFontSize: {
      '65-1': 8, // pt
      '21-3': 10, // pt
      '40-2': 9 // pt
    },
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold'
  });

  const [columnMapping, setColumnMapping] = useState<ColumnMappingSettings>({
    availableColumns: [],
    mappings: [],
    maxRows: 2
  });

  // Detect available columns from data
  useEffect(() => {
    if (data.length > 0) {
      const allColumns = new Set<string>();
      data.forEach(item => {
        Object.keys(item).forEach(key => allColumns.add(key));
      });
      
      const available = Array.from(allColumns);
      setColumnMapping(prev => ({
        ...prev,
        availableColumns: available,
        mappings: prev.mappings.length === 0 ? [
          // Try default mappings first
          { excelColumn: 'name', labelRow: 2, enabled: true },
          { excelColumn: 'surname', labelRow: 2, enabled: true },
          { excelColumn: 'text', labelRow: 1, enabled: true }
        ].filter(mapping => available.includes(mapping.excelColumn)).length > 0 ? 
        [
          { excelColumn: 'name', labelRow: 2, enabled: true },
          { excelColumn: 'surname', labelRow: 2, enabled: true },
          { excelColumn: 'text', labelRow: 1, enabled: true }
        ].filter(mapping => available.includes(mapping.excelColumn)) :
        // If no default columns exist, create mappings for first few available columns
        available.slice(0, Math.min(3, available.length)).map((col, index) => ({
          excelColumn: col,
          labelRow: index < 1 ? 1 : 2, // First column in row 1, rest in row 2
          enabled: true
        })) : prev.mappings
      }));
      
      // Auto-open controls when data is first loaded
      setSpacingAdjustments(prev => ({
        ...prev,
        showControls: true
      }));
    }
  }, [data]);
  const formatConfigs: Record<LabelFormat, LabelFormatConfig> = {
    '65-1': {
      id: '65-1',
      name: '65-1 Format',
      labelsPerSheet: 65,
      labelWidth: 36.0, // Slightly smaller than actual to account for spacing
      labelHeight: 20.0,
      columns: 5,
      rows: 13,
      marginTop: 9.0,
      marginLeft: 7.0,
      gapX: 2.5,
      gapY: 1.0
    },
    '21-3': {
      id: '21-3',
      name: '21-3 Format',
      labelsPerSheet: 21,
      labelWidth: 63.5,
      labelHeight: 36.0,
      columns: 3,
      rows: 7,
      marginTop: 15.0,
      marginLeft: 8.5,
      gapX: 2.5,
      gapY: 2.0
    },
    '40-2': {
      id: '40-2',
      name: '40-2 Format',
      labelsPerSheet: 40,
      labelWidth: 48.5,
      labelHeight: 25.4,
      columns: 4,
      rows: 10,
      marginTop: 8.0,
      marginLeft: -6.0,
      gapX: 1.5,
      gapY: 1.0
    }
  };

  const config = formatConfigs[format];
  
  // Group data into sheets
  const sheets: PersonData[][] = [];
  for (let i = 0; i < data.length; i += config.labelsPerSheet) {
    sheets.push(data.slice(i, i + config.labelsPerSheet));
  }



  return (
    <div className="label-preview">
      <div className="spacing-controls no-print">
        <button 
          onClick={() => setSpacingAdjustments(prev => ({...prev, showControls: !prev.showControls}))}
          className="toggle-controls-btn"
        >
          {spacingAdjustments.showControls ? '✕ Hide' : '⚙️ Customize'} Label Controls
        </button>
        
        {spacingAdjustments.showControls && (
          <div className="controls-panel">
            <div className="tabs">
              <button 
                className={`tab ${spacingAdjustments.activeTab === 'columns' ? 'active' : ''}`}
                onClick={() => setSpacingAdjustments(prev => ({...prev, activeTab: 'columns'}))}
              >
                📋 Columns
              </button>
              <button 
                className={`tab ${spacingAdjustments.activeTab === 'spacing' ? 'active' : ''}`}
                onClick={() => setSpacingAdjustments(prev => ({...prev, activeTab: 'spacing'}))}
              >
                📏 Spacing
              </button>
              <button 
                className={`tab ${spacingAdjustments.activeTab === 'fonts' ? 'active' : ''}`}
                onClick={() => setSpacingAdjustments(prev => ({...prev, activeTab: 'fonts'}))}
              >
                🔤 Fonts
              </button>
            </div>
            
            {spacingAdjustments.activeTab === 'columns' && (
              <div className="tab-content">
                <h4>📋 Column Mapping</h4>
                <p className="mapping-description">
                  Map Excel columns to label rows. Drag to reorder or change which row each column appears on.
                </p>
                
                <div className="mapping-section">
                  <h5>Number of Rows per Label:</h5>
                  <div className="control-group">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={columnMapping.maxRows}
                      onChange={(e) => {
                        const newMaxRows = Math.max(1, Math.min(10, parseInt(e.target.value) || 2));
                        setColumnMapping(prev => ({
                          ...prev,
                          maxRows: newMaxRows,
                          // Remove mappings that exceed the new maxRows
                          mappings: prev.mappings.filter(m => m.labelRow <= newMaxRows)
                        }));
                      }}
                      className="row-count-input"
                    />
                    <span>rows</span>
                  </div>
                </div>
                
                <div className="mapping-section">
                  <h5>Available Excel Columns:</h5>
                  <div className="available-columns">
                    {columnMapping.availableColumns.map(column => (
                      <span key={column} className="column-tag">
                        {column}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mapping-section">
                  <h5>Label Row Mappings:</h5>
                  {Array.from({ length: columnMapping.maxRows }, (_, rowIndex) => (
                    <div key={rowIndex} className="row-mapping">
                      <h6>Row {rowIndex + 1}:</h6>
                      <div className="row-controls">
                        {columnMapping.mappings
                          .filter(mapping => mapping.labelRow === rowIndex + 1)
                          .map((mapping, index) => (
                            <div key={mapping.excelColumn} className="mapping-control">
                              <select
                                value={mapping.excelColumn}
                                onChange={(e) => {
                                  const newMappings = [...columnMapping.mappings];
                                  const mappingIndex = newMappings.findIndex(m => m.excelColumn === mapping.excelColumn);
                                  if (mappingIndex !== -1) {
                                    newMappings[mappingIndex].excelColumn = e.target.value;
                                  }
                                  setColumnMapping(prev => ({...prev, mappings: newMappings}));
                                }}
                                className="column-select"
                              >
                                <option value="">Select column...</option>
                                {columnMapping.availableColumns.map(col => (
                                  <option key={col} value={col}>{col}</option>
                                ))}
                              </select>
                              
                              <select
                                value={mapping.labelRow}
                                onChange={(e) => {
                                  const newMappings = [...columnMapping.mappings];
                                  const mappingIndex = newMappings.findIndex(m => m.excelColumn === mapping.excelColumn);
                                  if (mappingIndex !== -1) {
                                    newMappings[mappingIndex].labelRow = parseInt(e.target.value);
                                  }
                                  setColumnMapping(prev => ({...prev, mappings: newMappings}));
                                }}
                                className="row-select"
                              >
                                {Array.from({ length: columnMapping.maxRows }, (_, i) => (
                                  <option key={i + 1} value={i + 1}>Row {i + 1}</option>
                                ))}
                              </select>
                              
                              <button
                                onClick={() => {
                                  const newMappings = columnMapping.mappings.filter(m => m.excelColumn !== mapping.excelColumn);
                                  setColumnMapping(prev => ({...prev, mappings: newMappings}));
                                }}
                                className="remove-mapping"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        
                        <button
                          onClick={() => {
                            const newMapping = {
                              excelColumn: '',
                              labelRow: rowIndex + 1,
                              enabled: true
                            };
                            setColumnMapping(prev => ({...prev, mappings: [...prev.mappings, newMapping]}));
                          }}
                          className="add-mapping"
                        >
                          + Add Column
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="preset-buttons">
                  <button
                    onClick={() => {
                      // Smart detection for diploma-related columns
                      const diplomaCol = columnMapping.availableColumns.find(col => 
                        col.toLowerCase().includes('text') || 
                        col.toLowerCase().includes('tekst') || 
                        col.toLowerCase().includes('број дипломе') || 
                        col.toLowerCase().includes('broj diplome') ||
                        col.toLowerCase().includes('diploma')
                      );
                      const nameCol = columnMapping.availableColumns.find(col => 
                        col.toLowerCase().includes('name') || 
                        col.toLowerCase().includes('име') ||
                        col.toLowerCase().includes('ime')
                      );
                      const surnameCol = columnMapping.availableColumns.find(col => 
                        col.toLowerCase().includes('surname') || 
                        col.toLowerCase().includes('презиме') ||
                        col.toLowerCase().includes('prezime')
                      );
                      
                      const newMappings: ColumnMapping[] = [];
                      // Row 1: Diploma number only
                      if (diplomaCol) newMappings.push({ excelColumn: diplomaCol, labelRow: 1, enabled: true });
                      // Row 2: Name and surname together
                      if (nameCol) newMappings.push({ excelColumn: nameCol, labelRow: 2, enabled: true });
                      if (surnameCol) newMappings.push({ excelColumn: surnameCol, labelRow: 2, enabled: true });
                      
                      setColumnMapping(prev => ({...prev, mappings: newMappings}));
                    }}
                    className="preset-btn"
                  >
                    📜 Diploma Preset
                  </button>
                  
                  <button
                    onClick={() => {
                      const predmetCol = columnMapping.availableColumns.find(col => 
                        col.toLowerCase().includes('предмет') || col.toLowerCase().includes('predmet')
                      );
                      const grupaCol = columnMapping.availableColumns.find(col => 
                        col.toLowerCase().includes('група') || col.toLowerCase().includes('grupa')
                      );
                      
                                             const newMappings: ColumnMapping[] = [];
                       if (predmetCol) newMappings.push({ excelColumn: predmetCol, labelRow: 1, enabled: true });
                       if (grupaCol) newMappings.push({ excelColumn: grupaCol, labelRow: 2, enabled: true });
                      
                      setColumnMapping(prev => ({...prev, mappings: newMappings}));
                    }}
                    className="preset-btn"
                  >
                    🎓 Subject/Group Preset
                  </button>
                </div>
              </div>
            )}

            {spacingAdjustments.activeTab === 'spacing' && (
              <div className="tab-content">
                <div className="sub-tabs">
                  <h4>📏 Row Spacing</h4>
                  {spacingAdjustments[format].rowSpacing.map((spacing: number, index: number) => (
                    <div key={index} className="control-group">
                      <label>After row {index + 1}:</label>
                      <input
                        type="range"
                        min="0"
                        max="30"
                        step="0.5"
                        value={spacing}
                        onChange={(e) => {
                          const newSpacing = [...spacingAdjustments[format].rowSpacing];
                          newSpacing[index] = parseFloat(e.target.value);
                          setSpacingAdjustments(prev => ({
                            ...prev, 
                            [format]: {...prev[format], rowSpacing: newSpacing}
                          }));
                        }}
                      />
                      <span>{spacing}mm</span>
                    </div>
                  ))}
                  
                  <h4>📐 Column Spacing</h4>
                  {spacingAdjustments[format].columnSpacing.map((spacing: number, index: number) => (
                    <div key={index} className="control-group">
                      <label>Column {index + 1} right margin:</label>
                      <input
                        type="range"
                        min="0"
                        max="15"
                        step="0.5"
                        value={spacing}
                        onChange={(e) => {
                          const newSpacing = [...spacingAdjustments[format].columnSpacing];
                          newSpacing[index] = parseFloat(e.target.value);
                          setSpacingAdjustments(prev => ({
                            ...prev, 
                            [format]: {...prev[format], columnSpacing: newSpacing}
                          }));
                        }}
                      />
                      <span>{spacing}mm</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {spacingAdjustments.activeTab === 'fonts' && (
              <div className="tab-content">
                <h4>🔤 Font Settings</h4>
                
                <div className="control-group">
                  <label>Row 1 font size:</label>
                  <input
                    type="range"
                    min="6"
                    max="48"
                    step="1"
                    value={fontSettings.diplomaFontSize[format]}
                    onChange={(e) => setFontSettings(prev => ({
                      ...prev,
                      diplomaFontSize: {...prev.diplomaFontSize, [format]: parseInt(e.target.value)}
                    }))}
                  />
                  <span>{fontSettings.diplomaFontSize[format]}pt</span>
                </div>
                
                <div className="control-group">
                  <label>Other rows font size:</label>
                  <input
                    type="range"
                    min="6"
                    max="48"
                    step="1"
                    value={fontSettings.nameFontSize[format]}
                    onChange={(e) => setFontSettings(prev => ({
                      ...prev,
                      nameFontSize: {...prev.nameFontSize, [format]: parseInt(e.target.value)}
                    }))}
                  />
                  <span>{fontSettings.nameFontSize[format]}pt</span>
                </div>
                
                <div className="control-group">
                  <label>Font family:</label>
                  <select
                    value={fontSettings.fontFamily}
                    onChange={(e) => setFontSettings(prev => ({...prev, fontFamily: e.target.value}))}
                    className="font-select"
                  >
                    <option value="Arial, sans-serif">Arial</option>
                    <option value="Times New Roman, serif">Times New Roman</option>
                    <option value="Helvetica, sans-serif">Helvetica</option>
                    <option value="Calibri, sans-serif">Calibri</option>
                    <option value="Georgia, serif">Georgia</option>
                    <option value="Verdana, sans-serif">Verdana</option>
                  </select>
                </div>
                
                <div className="control-group">
                  <label>Font weight:</label>
                  <select
                    value={fontSettings.fontWeight}
                    onChange={(e) => setFontSettings(prev => ({...prev, fontWeight: e.target.value}))}
                    className="font-select"
                  >
                    <option value="normal">Normal</option>
                    <option value="bold">Bold</option>
                    <option value="600">Semi Bold</option>
                    <option value="800">Extra Bold</option>
                  </select>
                </div>
              </div>
            )}
            
            <button 
              onClick={() => {
                setSpacingAdjustments(prev => ({
                  ...prev, 
                  '65-1': {
                    rowSpacing: Array.from({ length: 13 }, (_, i) => {
                      if (i >= 9) return 20;
                      if (i >= 5) return 10;
                      return 1;
                    }) as number[],
                    columnSpacing: [2.5, 2.5, 2.5, 4, 4] as number[]
                  },
                  '21-3': {
                    rowSpacing: Array.from({ length: 7 }, () => 2) as number[],
                    columnSpacing: [2.5, 2.5, 2.5] as number[]
                  },
                  '40-2': {
                    rowSpacing: Array.from({ length: 10 }, () => 2) as number[],
                    columnSpacing: [2.5, 2.5, 2.5, 2.5] as number[]
                  }
                }));
                setFontSettings({
                  diplomaFontSize: {'65-1': 9, '21-3': 11, '40-2': 10},
                  nameFontSize: {'65-1': 8, '21-3': 10, '40-2': 9},
                  fontFamily: 'Arial, sans-serif',
                  fontWeight: 'bold'
                });
              }}
              className="reset-btn"
            >
              🔄 Reset All to Default
            </button>
          </div>
        )}
      </div>
      
      <style dangerouslySetInnerHTML={{
        __html: `
          /* Font Settings */
          .diploma-line {
            font-size: ${fontSettings.diplomaFontSize[format]}pt !important;
            font-family: ${fontSettings.fontFamily} !important;
            font-weight: ${fontSettings.fontWeight} !important;
          }
          .name-line {
            font-size: ${fontSettings.nameFontSize[format]}pt !important;
            font-family: ${fontSettings.fontFamily} !important;
            font-weight: ${fontSettings.fontWeight} !important;
          }
          
          /* Spacing Settings */
          ${format === '65-1' ? `
            ${spacingAdjustments[format].rowSpacing.map((spacing: number, index: number) => `
              .labels-grid-65-1 .label:nth-child(n+${(index + 1) * 5 + 1}) {
                margin-top: ${spacing}mm !important;
              }
            `).join('')}
            ${spacingAdjustments[format].columnSpacing.map((spacing: number, index: number) => `
              .labels-grid-65-1 .label:nth-child(5n+${index + 1}) {
                margin-left: ${spacing}mm !important;
              }
            `).join('')}
          ` : ''}
          
          ${format === '21-3' ? `
            ${spacingAdjustments[format].rowSpacing.map((spacing: number, index: number) => `
              .labels-grid-21-3 .label:nth-child(n+${(index + 1) * 3 + 1}) {
                margin-top: ${spacing}mm !important;
              }
            `).join('')}
            ${spacingAdjustments[format].columnSpacing.map((spacing: number, index: number) => `
              .labels-grid-21-3 .label:nth-child(3n+${index + 1}) {
                margin-left: ${spacing}mm !important;
              }
            `).join('')}
          ` : ''}
          
          ${format === '40-2' ? `
            ${spacingAdjustments[format].rowSpacing.map((spacing: number, index: number) => `
              .labels-grid-40-2 .label:nth-child(n+${(index + 1) * 4 + 1}) {
                margin-top: ${spacing}mm !important;
              }
            `).join('')}
            ${spacingAdjustments[format].columnSpacing.map((spacing: number, index: number) => `
              .labels-grid-40-2 .label:nth-child(4n+${index + 1}) {
                margin-left: ${spacing}mm !important;
              }
            `).join('')}
          ` : ''}
        `
      }} />
      
      {columnMapping.mappings.filter(m => m.enabled && m.excelColumn).length === 0 && (
        <div className="no-mappings-message">
          <h3>⚠️ Nema podešenih kolona</h3>
          <p>
            Kliknite na "⚙️ Customize Label Controls" dugme iznad i idite na "📋 Columns" tab da podesite koje Excel kolone se prikazuju na nalepnicama.
          </p>
        </div>
      )}
      
      {sheets.map((sheetData, sheetIndex) => (
        <div key={sheetIndex} className="sheet" data-format={format}>
          <div 
            className={`labels-grid ${format === '65-1' ? 'labels-grid-65-1' : format === '21-3' ? 'labels-grid-21-3' : format === '40-2' ? 'labels-grid-40-2' : ''}`}
            style={{
              marginTop: `${config.marginTop}mm`,
              marginLeft: `${config.marginLeft}mm`,
              marginRight: `${config.marginLeft}mm`,
              marginBottom: `${config.marginTop}mm`,
              gap: `${config.gapY}mm ${config.gapX}mm`,
              gridTemplateColumns: `repeat(${config.columns}, ${config.labelWidth}mm)`,
              gridTemplateRows: `repeat(${config.rows}, ${config.labelHeight}mm)`
            }}
          >
            {Array.from({ length: config.labelsPerSheet }, (_, index) => {
              const person = sheetData[index];
              return (
                <div 
                  key={index}
                  className="label"
                  style={{
                    width: `${config.labelWidth}mm`,
                    height: `${config.labelHeight}mm`
                  }}
                >
                  {person && (
                    <div className="label-content">
                      {Array.from({ length: columnMapping.maxRows }, (_, rowIndex) => {
                        const rowMappings = columnMapping.mappings
                          .filter(mapping => mapping.labelRow === rowIndex + 1 && mapping.enabled && mapping.excelColumn)
                          .map(mapping => person[mapping.excelColumn])
                          .filter(value => value != null && value !== '')
                          .map(value => String(value).trim())
                          .filter(value => value.length > 0);
                        
                        if (rowMappings.length === 0) return null;
                        
                        return (
                          <div 
                            key={rowIndex} 
                            className={rowIndex === 0 ? "diploma-line" : "name-line"}
                          >
                            {rowMappings.join(' ')}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LabelPreview; 